<template>
  <div class="flex flex-col justify-between flex-1">
    <div class="divide-y divide-gray-200">
      <div class="pt-6 pb-5 space-y-6">
        <Form
          v-slot="{ values }"
          :validation-schema="schema"
          :initial-values="initial_values"
          class="mt-2 space-y-6"
          @submit="createExternalScore">
          <div class="flex flex-col items-start justify-between gap-1 mb-5 rounded-lg">
            <div class="flex flex-row justify-between w-full gap-1 mb-5 rounded-lg">
              <TextInput :label="$t('Name')"
                         name="name"
                         class="w-[50%]"
                         type="text"
                         :placeholder="$t('NPS name')" />
              <SelectInput
                :value="values['category']"
                :is-box-background="true"
                class="w-[50%] !z-50"
                label-attr="label"
                value-attr="value"
                :label="$t('Score Type')"
                :input-classes="'text-left'"
                :show-label="true"
                name="score_type"
                :allow-null="false"
                :items="[
                  { label: $t('Scale 10 NPS'), value: 'scale_10' },
                  { label: $t('Scale 5 NPS'), value: 'scale_5' }
                ]" />
            </div>
            <div class="flex justify-between w-full min-h-[50px]">
              <div class="flex items-center mt-0 mr-6 gap-x-2">
                <Switch
                  v-model="use_default_metric"
                  :class="[
                    use_default_metric ? 'bg-blue' : 'bg-gray-200',
                    'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
                  ]">
                  <span
                    aria-hidden="true"
                    :class="[
                      use_default_metric && $store.state.portfolio.ui_options?.language == 'ar'
                        ? '-translate-x-5'
                        : use_default_metric
                          ? 'translate-x-5'
                          : 'translate-x-0',
                      'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                    ]" />
                </Switch>
                <span class="dark:text-common-grey">{{ $t("Use Default NPS Metric") }}</span>
              </div>

              <ComboBoxInput
                v-if="!use_default_metric"
                :items="
                  $store.state.metrics.filter(
                    (x) =>
                      (x.object_type == 'contact' &&
                        x.data_type == 'Number' &&
                        x.input_type != 'BarRating' &&
                        x.input_type != 'StarRating') ||
                      (x.object_type == 'contact' &&
                        x.data_type == 'Number' &&
                        x.input_type == 'BarRating' &&
                        values['score_type'] == 'scale_10') ||
                      (x.object_type == 'contact' &&
                        x.data_type == 'Number' &&
                        x.input_type == 'StarRating' &&
                        values['score_type'] == 'scale_5')
                  )
                "
                name="metric"
                :description="$t('Attached metric is a metric that will be used to save and keep track of your contacts NPS responses.')"
                label-attr="name"
                value-attr="id"
                classes="w-full relative dark:text-white !w-[50%]"
                :is-box-background="true"
                :label="$t('Attached metric')"
                :show-label="true"
                :floating-label="false"
                :allow-null="false" />
            </div>

            <div class="justify-between gap-2 rounded-md border-0.5 dark:border-dark-divider-border h-10 w-full flex mt-5">
              <TextInput
                :label="$t('From label')"
                name="from_label"
                class="w-[50%]"
                type="text"
                :placeholder="`${$store.state.organization.name} ` + $t('CSM Team')" />
              <TextInput
                :label="$t('Subject line')"
                name="subject_line"
                class="w-[50%]"
                type="text"
                :placeholder="
                  $t('Rate your Experience with') + ` ${$store.state.organization.name} ` + $t('to help us better serve you!')
                " />
            </div>

            <div class="rounded-md border-0.5 dark:border-dark-divider-border h-28 w-full flex mt-5">
              <TextArea
                :placeholder="
                  $t('How likely is it that you would recommend') + ` ${$store.state.organization.name} ` + $t('to a friend or colleague?')
                "
                name="header_message"
                :value="values['header_message']"
                class="mt-0 border-0.5 dark:border-dark-divider-border w-full h-full border-1"
                :label="$t('Email header message')" />
            </div>

            <div class="rounded-md border-0.5 dark:border-dark-divider-border h-28 w-full flex mt-5">
              <TextArea
                :placeholder="
                  $t(
                    'Thank you for your feedback! This will be shared with our team and we will continue to do our best to provide you with the best experience possible.'
                  )
                "
                :value="values['success_message']"
                name="success_message"
                class="mt-0 border-0.5 dark:border-dark-divider-border w-full h-full border-1"
                :label="$t('Submit Success Message')" />
            </div>

            <div class="flex flex-col w-full">
              <div class="mt-3 mb-5">
                <ContactsSelect
                  v-model:selected-contacts="selected_contacts"
                  :description="$t('Select your target audience')"
                  :label="$t('Contacts')" />
              </div>
            </div>

            <div class="flex flex-row justify-between w-full gap-2">
              <button type="button" class="w-1/2 m-0 cancel-btn" @click="$emit('cancel')">{{ $t("Cancel") }}</button>
              <button type="submit" class="w-1/2 primary-btn">
                <RotateCw class="w-5 h-5 mr-3 -ml-1 opacity-0" aria-hidden="true" />
                <span class="mr-5"> {{ $t("Create and Send NPS") }} </span>
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>

<script>
import * as Yup from "yup"
import SelectInput from "@/components/shared/SelectInput.vue"
import TextInput from "@/components/shared/TextInput.vue"
import TextArea from "@/components/shared/TextArea.vue"
import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import { Form } from "vee-validate"
import { RotateCw } from "lucide-vue-next"
import ContactsSelect from "@/components/shared/ContactsSelect.vue"
import dataService from "@/services/data.service"
import { Switch } from "@headlessui/vue"
export default {
  components: { Switch, TextInput, TextArea, SelectInput, ComboBoxInput, Form, RotateCw, ContactsSelect },

  props: {
    records: {
      type: Array,
      required: false
    },
    objectType: {
      type: String,
      required: false
    },
    excludedRecords: {
      type: Array,
      default: () => []
    },
    isAllRecords: {
      type: Boolean,
      default: false
    }
  },
  emits: ["cancel", "submitted", "error", "done"],
  setup() {
    return {}
  },
  data() {
    return {
      use_default_metric: true,
      selected_contacts: [],
      initial_values: {},
      schema: null
    }
  },

  created() {
    this.schema = Yup.object().shape({
      subject_line: Yup.string().required(this.$t("subject line is required")),
      from_label: Yup.string().required(this.$t("from label is required")),
      header_message: Yup.string().required(this.$t("header message is required")),
      success_message: Yup.string().required(this.$t("success message is required")),
      name: Yup.string().required(this.$t("name is required")),
      score_type: Yup.string().required(this.$t("score type is required")),
      // metric required if use_default_metric is false
      metric: Yup.string().when("use_default_metric", {
        is: false,
        then: Yup.string().required(this.$t("metric is required"))
      }),
      records: Yup.array().of(Yup.string())
    })
    if (this.records?.length) {
      this.selected_contacts = this.records
    }

    dataService
      .getSurveys({ page_size: 1 })
      .then((res) => {
        if (res.data.results.length > 0) {
          this.initial_values = res.data.results[0]
        } else {
          this.initial_values = {
            name: this.$t("NPS Response"),
            from_label: `${this.$store.state.organization.name} ` + this.$t("CSM Team"),
            subject_line:
              this.$t("Rate your Experience with") + ` ${this.$store.state.organization.name} ` + this.$t("to help us better serve you!"),
            score_type: "scale_10",
            header_message:
              this.$t("How likely is it that you would recommend") +
              ` ${this.$store.state.organization.name} ` +
              this.$t("to a friend or colleague?"),
            success_message: this.$t(
              "Thank you for your feedback! This will be shared with our team and we will continue to do our best to provide you with the best experience possible."
            )
          }
        }
      })
      .catch(() => {
        this.initial_values = {
          name: this.$t("NPS Response"),
          from_label: `${this.$store.state.organization.name} ` + this.$t("CSM Team"),
          subject_line:
            this.$t("Rate your Experience with") + ` ${this.$store.state.organization.name} ` + this.$t("to help us better serve you!"),
          score_type: "scale_10",
          header_message:
            this.$t("How likely is it that you would recommend") +
            ` ${this.$store.state.organization.name} ` +
            this.$t("to a friend or colleague?"),
          success_message: this.$t(
            "Thank you for your feedback! This will be shared with our team and we will continue to do our best to provide you with the best experience possible."
          )
        }
      })
  },
  methods: {
    createExternalScore(values) {
      let data = values
      data["use_default_metric"] = this.use_default_metric
      data["records"] = this.selected_contacts.map((x) => x.id)

      data["is_all_records"] = this.isAllRecords

      data["excluded_records"] = this.excludedRecords
      data["object_type"] = this.objectType
      dataService.createSurvey(data).then((res) => {
        this.$emit("done", res)
      })
    }
  }
}
</script>
